import { type Ref, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { formatClock, formatWatching, watchingPrefix } from "../../../shared/watching/line.ts";
import { audioPathOf, bindFile, type BindingStore, clearBinding, isVideoFile, matchesBinding, readBinding, type VideoBinding, withStopFired, writeBinding } from "./videoBinding";
import { useWorkspaceText } from "../components/useWorkspaceJson";
import { crossedStop, parseStops, stopMessage, stopsPathFor, type VideoStop } from "./videoStops";
import { NotesBar } from "./NotesBar";
import { notesPathFor } from "./videoNotes";
import { canAnnounce, closingMessage, playedSeconds, watchedEnough } from "./videoClosing";
import { audioNameFor, openingMessage } from "./videoOpening";

/** What the chat asks of the panel: where the video stands, and to stop or move it. */
export interface VideoPanelHandle {
	/** The watching line for the next message, or nothing when no video has been played. */
	prefix(): string;
	pause(): void;
	seek(seconds: number): void;
}

/** Touching `localStorage` can itself throw; the binding functions catch what these let through. */
const browserStore: BindingStore = {
	getItem: (key) => window.localStorage.getItem(key),
	setItem: (key, value) => window.localStorage.setItem(key, value),
	removeItem: (key) => window.localStorage.removeItem(key),
};

/** How often the place is saved while the video plays. */
const SAVE_EVERY_MS = 5000;

type Audio =
	| { stage: "idle" | "done" }
	| { stage: "extracting"; share: number }
	| { stage: "uploading" }
	/** Uploaded, and the opening could not be sent: a run is on, or someone else holds the session. */
	| { stage: "queued"; why: string }
	| { stage: "failed"; why: string; noAudio: boolean };

/**
 * A local video beside the chat. The file never leaves the browser; on a profile with a video
 * opening its sound is extracted, uploaded, and announced to the agent in one message.
 */
export function VideoPanel({
	ref,
	sessionId,
	file,
	opening,
	closing,
	upload,
	send,
	busy,
	onPick,
	onClose,
}: {
	ref: Ref<VideoPanelHandle>;
	sessionId: string;
	/** The chosen video. Null while the session only remembers one from before. */
	file: File | null;
	/** The profile's `video.opening`. Null means the panel only plays. */
	opening: string | null;
	/** The profile's `video.closing`, under a project. Null means a finished video says nothing. */
	closing: string | null;
	/** Resolves to the uploaded file's workspace path. */
	upload: (file: File) => Promise<string>;
	/** Resolves to null once sent, or to why it was not. */
	send: (text: string) => Promise<string | null>;
	/** A run is on, or someone else holds the session. */
	busy: boolean;
	onPick: (file: File) => void;
	onClose: () => void;
}) {
	const video = useRef<HTMLVideoElement>(null);
	const picker = useRef<HTMLInputElement>(null);
	const played = useRef(false);
	const lastSaved = useRef(0);
	/** Kept across retries, so a failed upload or send never extracts twice. */
	const extracted = useRef<File | null>(null);
	const audioPath = useRef<string | null>(null);

	const [url, setUrl] = useState<string | null>(null);
	const [binding, setBinding] = useState<VideoBinding | null>(() => readBinding(browserStore, sessionId));
	/** The chosen file is not the one this session was watching, and nobody has said to go on. */
	const [mismatch, setMismatch] = useState(false);
	const [unplayable, setUnplayable] = useState(false);
	const [collapsed, setCollapsed] = useState(false);
	const [audio, setAudio] = useState<Audio>({ stage: "idle" });
	/** Where the video stands, to the second, for the notes under it. */
	const [now, setNow] = useState(0);
	const [notesRevision, setNotesRevision] = useState(0);

	// The agent writes notes during a run, so the end of one is when to read them again.
	useEffect(() => {
		if (!busy) setNotesRevision((v) => v + 1);
	}, [busy]);

	// Where the video stops by itself, at the end of each section it teaches (ADR-0041). Read again with
	// the notes, since the agent writes both.
	const stopsAudio = opening && binding ? audioPathOf(binding) : null;
	const { data: stopsText } = useWorkspaceText(sessionId, stopsAudio ? stopsPathFor(stopsAudio) : undefined, notesRevision);
	const stops = useMemo(() => parseStops(stopsText ?? ""), [stopsText]);
	/** Where the last time update left the video, so a stop is made only by playing across it. */
	const lastTime = useRef(0);

	useEffect(() => {
		if (!file) return;
		const next = URL.createObjectURL(file);
		setUrl(next);
		setUnplayable(false);
		setAudio({ stage: "idle" });
		extracted.current = null;
		audioPath.current = null;
		played.current = false;
		const remembered = readBinding(browserStore, sessionId);
		if (remembered && !matchesBinding(remembered, file)) setMismatch(true);
		else rebind(bindFile(remembered, file));
		return () => URL.revokeObjectURL(next);
	}, [file, sessionId]);

	function rebind(next: VideoBinding) {
		setMismatch(false);
		setBinding(next);
		writeBinding(browserStore, sessionId, next);
	}

	function save(force: boolean) {
		const el = video.current;
		if (!el || !file || mismatch || !binding) return;
		const now = Date.now();
		if (!force && now - lastSaved.current < SAVE_EVERY_MS) return;
		lastSaved.current = now;
		const seconds = Math.floor(el.currentTime);
		// From the latest binding, not this render's: a pause event can land after a stop was recorded.
		setBinding((prev) => {
			if (!prev) return prev;
			const next = { ...prev, seconds };
			writeBinding(browserStore, sessionId, next);
			return next;
		});
	}

	useImperativeHandle(ref, () => ({
		prefix: () => {
			const el = video.current;
			if (!el || !file || unplayable) return "";
			return watchingPrefix({ file: file.name, seconds: el.currentTime, played: played.current });
		},
		pause: () => video.current?.pause(),
		seek: (seconds) => {
			const el = video.current;
			if (!el) return;
			setCollapsed(false);
			el.pause();
			el.currentTime = seconds;
			played.current = true;
		},
	}));

	/** Extract, upload, announce -- each step picked up where the last attempt left it. */
	async function sendAudio() {
		const el = video.current;
		if (!file || !opening || !el) return;
		try {
			if (!extracted.current) {
				setAudio({ stage: "extracting", share: 0 });
				const { extractAudio } = await import("./extractAudio");
				extracted.current = await extractAudio(file, audioNameFor(file.name), el.duration, (share) => setAudio({ stage: "extracting", share }));
			}
			if (!audioPath.current) {
				setAudio({ stage: "uploading" });
				audioPath.current = await upload(extracted.current);
			}
		} catch (err) {
			const noAudio = (err as Error).name === "NoAudioError";
			setAudio({ stage: "failed", why: noAudio ? "此视频无音轨" : (err as Error).message || "提取音频失败", noAudio });
			return;
		}
		const refused = await send(openingMessage(opening, audioPath.current, file.name));
		if (refused) return setAudio({ stage: "queued", why: refused });
		setAudio({ stage: "done" });
		setBinding((prev) => {
			if (!prev) return prev;
			const next = { ...prev, audioUploaded: true, audioPath: audioPath.current ?? undefined };
			writeBinding(browserStore, sessionId, next);
			return next;
		});
	}

	/** Says the video is finished, once. A refusal leaves the button there to press again. */
	async function announceLearned() {
		if (!file || !closing || !canAnnounce(closing, binding)) return;
		video.current?.pause();
		if (await send(closingMessage(closing, file.name))) return;
		setBinding((prev) => {
			if (!prev) return prev;
			const next = { ...prev, learned: true };
			writeBinding(browserStore, sessionId, next);
			return next;
		});
	}

	function onEnded() {
		const el = video.current;
		if (!el) return;
		const ranges = Array.from({ length: el.played.length }, (_, i) => [el.played.start(i), el.played.end(i)] as const);
		if (watchedEnough(playedSeconds(ranges), el.duration)) void announceLearned();
	}

	/** Played across the end of a section: stop, and tell the agent so it can ask about it. Once each. */
	async function makeStop(stop: VideoStop) {
		const el = video.current;
		if (!el || !file) return;
		el.pause();
		if (await send(stopMessage(file.name, stop.at))) return;
		setBinding((prev) => {
			if (!prev) return prev;
			const next = withStopFired(prev, stop.at);
			writeBinding(browserStore, sessionId, next);
			return next;
		});
	}

	function onTimeUpdate(el: HTMLVideoElement) {
		setNow(Math.floor(el.currentTime));
		save(false);
		const from = lastTime.current;
		lastTime.current = el.currentTime;
		if (busy || mismatch || !binding) return;
		const stop = crossedStop(stops, from, el.currentTime, binding.stopsFired ?? []);
		if (stop) void makeStop(stop);
	}

	/** Asks for a note at the moment the video stands at, in the words the `watch` skill listens for. */
	function askForNote(): Promise<string | null> {
		const el = video.current;
		if (!el || !file) return Promise.resolve(null);
		el.pause();
		return send(`${formatWatching(file.name, el.currentTime)}记笔记`);
	}

	function onLoaded() {
		const el = video.current;
		if (!el || !binding || mismatch) return;
		if (binding.seconds > 0 && binding.seconds < el.duration) {
			el.currentTime = binding.seconds;
			played.current = true;
		}
		if (opening && !binding.audioUploaded && audio.stage === "idle") void sendAudio();
	}

	// A mismatch answered with "go on" rebinds after the metadata already loaded.
	useEffect(() => {
		if (!mismatch && video.current && video.current.readyState >= 1) onLoaded();
	}, [mismatch]);

	const pickInput = (
		<input
			ref={picker}
			type="file"
			accept="video/*,.mkv,.flv,.avi,.wmv"
			hidden
			onChange={(e) => {
				const chosen = e.target.files?.[0];
				e.target.value = "";
				if (chosen && isVideoFile(chosen)) onPick(chosen);
			}}
		/>
	);

	if (!file) {
		if (!binding) return null;
		return (
			<aside className="video-panel remembered">
				{pickInput}
				<p className="hint">
					上次看到 {formatClock(binding.seconds)}，视频仅存于本机，请重新选择
				</p>
				<div className="video-actions">
					<button onClick={() => picker.current?.click()}>重新选择 {binding.name}</button>
					<button
						className="ghost"
						onClick={() => {
							clearBinding(browserStore, sessionId);
							onClose();
						}}
					>
						关闭
					</button>
				</div>
			</aside>
		);
	}

	const uploadedAudio = opening && binding ? audioPathOf(binding) : null;
	const notesSource = uploadedAudio ? notesPathFor(uploadedAudio) : null;

	return (
		<aside className={`video-panel${collapsed ? " collapsed" : ""}`}>
			{pickInput}
			<div className="video-head">
				<span className="video-name" title={file.name}>
					{file.name}
				</span>
				<button
					className="ghost"
					onClick={() => {
						video.current?.pause();
						setCollapsed((v) => !v);
					}}
				>
					{collapsed ? "展开" : "收起"}
				</button>
				<button className="ghost" title="关闭视频" onClick={onClose}>
					×
				</button>
			</div>
			{url && (
				<video
					ref={video}
					src={url}
					controls
					playsInline
					hidden={collapsed || unplayable}
					onLoadedMetadata={onLoaded}
					onPlay={() => (played.current = true)}
					onPause={() => save(true)}
					onEnded={onEnded}
					onTimeUpdate={(e) => onTimeUpdate(e.currentTarget)}
					onError={() => setUnplayable(true)}
				/>
			)}
			{unplayable && <p className="video-note">不支持此格式，请转为 H.264 MP4</p>}
			{mismatch && (
				<p className="video-note">
					与上次的文件不同（上次为 {binding?.name}）
					<button className="ghost" onClick={() => rebind(bindFile(null, file))}>
						使用此文件
					</button>
					<button className="ghost" onClick={() => picker.current?.click()}>
						重新选择
					</button>
				</p>
			)}
			{!unplayable && <AudioNote audio={audio} onRetry={() => void sendAudio()} />}
			{!mismatch && canAnnounce(closing, binding) && (
				<div className="video-actions">
					<button disabled={busy} onClick={() => void announceLearned()}>
						完成学习
					</button>
				</div>
			)}
			{notesSource && !collapsed && !unplayable && !mismatch && (
				<NotesBar
					key={notesSource}
					sessionId={sessionId}
					source={notesSource}
					revision={notesRevision}
					seconds={now}
					busy={busy}
					onSeek={(seconds) => {
						if (video.current) video.current.currentTime = seconds;
					}}
					onAdd={askForNote}
				/>
			)}
		</aside>
	);
}

function AudioNote({ audio, onRetry }: { audio: Audio; onRetry: () => void }) {
	switch (audio.stage) {
		case "extracting":
			return (
				<p className="video-note">
					正在提取音频 {Math.round(audio.share * 100)}%
					<progress value={audio.share} max={1} />
				</p>
			);
		case "uploading":
			return <p className="video-note">正在上传音频…</p>;
		case "queued":
			return (
				<p className="video-note">
					音频已上传，转写请求未发送：{audio.why}
					<button className="ghost" onClick={onRetry}>
						发送转写请求
					</button>
				</p>
			);
		case "failed":
			return (
				<p className="video-note">
					{audio.why}，可用 + 上传字幕（.srt）或转写稿
					{!audio.noAudio && (
						<button className="ghost" onClick={onRetry}>
							重试
						</button>
					)}
				</p>
			);
		default:
			return null;
	}
}
